import React from 'react';
import PropTypes from 'prop-types';
import { useRequestData, useLabels } from 'wsm-common-data';
import { setClassNames } from '@ddc/classnames-js';
import { trackEvent } from 'ddc-track-event';
import VehicleData from './VehicleData';
import TradeMarkup from './TradeMarkup';

const VehicleCard = ({ offerData }) => {
	const { deviceType, windowId, widgetName } = useRequestData();
	const { trade, offer, disclaimer, vdpLink } = offerData;
	const labels = useLabels();
	const isDesktop = deviceType === 'DESKTOP';
	const tradeSectionClasses = [isDesktop ? '' : 'text-center'];
	const tradeClasses = [
		'text-left',
		'text-black',
		!isDesktop ? 'ddc-font-size-large my-3 mx-0 mb-5' : null
	];
	const vehicleCardContainerClasses = [
		isDesktop
			? 'vehicle-card-container d-flex flex-row-reverse justify-content my-4 border border-light'
			: ''
	];
	const vehicleImageContainerClasses = [
		'vehicle-image-container',
		isDesktop
			? 'px-0 col-xs-6 bg-black d-flex align-items-center'
			: 'border border-bottom-0 bg-white'
	];
	const vehicleDataContainerClasses = [
		'vehicle-data-container',
		'text-center',
		isDesktop ? 'px-0 col-xs-6' : 'mb-3'
	];
	const inlineVehicleImageContainer = isDesktop ? {} : { minHeight: '200px' };
	const imageContainer = [
		'image-container',
		'w-100',
		isDesktop ? '' : 'd-flex justify-content-center'
	];
	const vehicleImageClasses = ['w-100'];
	const handleClick = () => {
		trackEvent(widgetName, windowId, {
			element: 'Vehicle photo',
			result: 'Navigated to VDP'
		});
	};
	const imagePath = offerData.offer.vehicle.inventoryVehiclePhoto;
	const imgSrc = `${imagePath}?impolicy=downsize_bkpt&w=410`;

	return (
		<div className={setClassNames(tradeSectionClasses)}>
			<div className={setClassNames(vehicleCardContainerClasses)}>
				<div
					className={setClassNames(vehicleImageContainerClasses)}
					style={inlineVehicleImageContainer}
				>
					<a
						onClick={handleClick}
						href={vdpLink}
						className={setClassNames(imageContainer)}
					>
						<img
							className={setClassNames(vehicleImageClasses)}
							src={imgSrc}
							alt={labels.get('VIEW_VEHICLE_DETAILS')}
						/>
					</a>
				</div>
				<div className={setClassNames(vehicleDataContainerClasses)}>
					<VehicleData offer={offer} disclaimer={disclaimer} />
				</div>
			</div>
			<TradeMarkup vehicleData={trade.vehicle} />
			<p className={setClassNames(tradeClasses)}>
				{labels.get('YOUR_TRADE_EQUITY')}
			</p>
			<p className={setClassNames(tradeClasses)}>
				{labels.get('ANY_QUESTIONS')}
			</p>
		</div>
	);
};

VehicleCard.propTypes = {
	offerData: PropTypes.shape({
		locale: PropTypes.string,
		vdpLink: PropTypes.string,
		offer: PropTypes.shape({
			vehicle: PropTypes.shape({
				inventoryVehiclePhoto: PropTypes.string
			})
		}),
		customer: PropTypes.shape({
			firstName: PropTypes.string.isRequired
		}),
		trade: PropTypes.shape({
			vehicle: PropTypes.shape({
				year: PropTypes.oneOfType([
					PropTypes.string.isRequired,
					PropTypes.number.isRequired
				]),
				make: PropTypes.string.isRequired,
				model: PropTypes.string.isRequired,
				trim: PropTypes.string.isRequired,
				estimatedEquity: PropTypes.number.isRequired,
				equityConsumptionEnabled: PropTypes.bool.isRequired,
				canTradeVehicle: PropTypes.bool.isRequired
			})
		}),
		disclaimer: PropTypes.shape({
			loanCreditScore: PropTypes.string,
			leaseCreditScore: PropTypes.string,
			APR: PropTypes.string,
			term: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			leaseTerm: PropTypes.oneOfType([
				PropTypes.string,
				PropTypes.number
			]),
			monthlyPaymentPerThousand: PropTypes.string,
			downPayment: PropTypes.string,
			lenderLegalName: PropTypes.string,
			leaseLenderLegalName: PropTypes.string,
			loanProgramEndDate: PropTypes.string,
			leaseProgramEndDate: PropTypes.string,
			dealershipName: PropTypes.string,
			offerType: PropTypes.string,
			leaseDueAtSigning: PropTypes.string,
			acquisitionFee: PropTypes.string,
			capCostTotal: PropTypes.string,
			totalMonthlyPayments: PropTypes.string,
			capCostReduction: PropTypes.string,
			leaseSecurityDeposit: PropTypes.string,
			monthlyPayment: PropTypes.string,
			excessMileCharge: PropTypes.string,
			annualMiles: PropTypes.string
		})
	}),
	vdpState: PropTypes.shape({
		isLoading: PropTypes.bool,
		href: PropTypes.string
	})
};

export default VehicleCard;
